import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';

import { useFeatureFlagsAdmin } from '../../hooks';
import { ADMIN_ACTION_PASSWORD_MIN_LENGTH, FLAG_KEY_REGEX } from './constants';
import { FEATURE_FLAGS_CONTAINER_SX, FLAG_ROW_SX } from './styles';

import type { FeatureFlagsTabProps } from './types';

export function FeatureFlagsTab({ featureFlags }: FeatureFlagsTabProps) {
  const { flags, loading, loadFlags, toggleFlag, addFlag } = featureFlags;
  const loadFlagsRef = useRef(loadFlags);

  const [password, setPassword] = useState('');
  const [newKey, setNewKey] = useState('');
  const [keyError, setKeyError] = useState('');
  const hasPassword = password.length >= ADMIN_ACTION_PASSWORD_MIN_LENGTH;

  useEffect(() => {
    loadFlagsRef.current();
  }, []);

  function handleAdd() {
    if (!hasPassword) return;

    const trimmed = newKey.trim();
    if (!FLAG_KEY_REGEX.test(trimmed)) {
      const msg =
        'Flag key must start with a lowercase letter and contain only lowercase letters, digits, or underscores (max 64 chars).';
      setKeyError(msg);
      toast.error(msg);
      return;
    }
    setKeyError('');
    addFlag(trimmed, password);
    setNewKey('');
  }

  const flagEntries = Object.entries(flags);

  return (
    <Box sx={FEATURE_FLAGS_CONTAINER_SX}>
      <TextField
        size="small"
        label="Password"
        type="password"
        autoComplete="off"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        helperText="Enter the admin password to change feature flags."
        inputProps={{
          'aria-label': 'feature flags password',
          minLength: ADMIN_ACTION_PASSWORD_MIN_LENGTH,
        }}
        sx={{ maxWidth: 360 }}
      />

      {loading ? (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pb: 2 }}>
            <TextField
              size="small"
              label="New flag key"
              value={newKey}
              onChange={(event) => {
                setNewKey(event.target.value);
                if (keyError) setKeyError('');
              }}
              error={Boolean(keyError)}
              helperText={keyError || undefined}
              inputProps={{ 'aria-label': 'new flag key' }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleAdd();
              }}
            />
            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={!hasPassword}
              sx={{ mt: 0.5 }}
            >
              Add
            </Button>
          </Box>

          {flagEntries.length === 0 ? (
            <Typography color="text.secondary">No feature flags defined yet.</Typography>
          ) : (
            flagEntries.map(([key, enabled]) => (
              <Box key={key} sx={FLAG_ROW_SX}>
                <Typography>{key}</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabled}
                      disabled={!hasPassword}
                      onChange={() => toggleFlag(key, !enabled, password)}
                      inputProps={{ 'aria-label': key }}
                    />
                  }
                  label={enabled ? 'Enabled' : 'Disabled'}
                />
              </Box>
            ))
          )}
        </>
      )}
    </Box>
  );
}

function FeatureFlagsTabContainer() {
  return <FeatureFlagsTab featureFlags={useFeatureFlagsAdmin()} />;
}

export default FeatureFlagsTabContainer;
